import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResult } from '../../../../shared/models/api-result.model';
import {
  CreateTaxRate,
  TaxRate,
  UpdateTaxRate,
} from '../../../models/tax-rate.model';
import { TaxRateService } from '../../../services/tax-rate.service';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { AddressService } from '../../../../shared/services/address.service';

@Component({
  selector: 'app-tax-rate-create-or-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ToolbarModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    ProgressSpinnerModule,
    ToastModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
  ],
  templateUrl: './tax-rate-create-or-update.component.html',
})
export class TaxRateCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private taxRateService = inject(TaxRateService);
  private formGroupService = inject(FormGroupService);
  private addressService = inject(AddressService);

  readonly countries = [{ value: 'US', label: 'Estados Unidos' }];
  readonly states = this.addressService
    .getStates()
    .filter((state) => ['FL', 'MA', 'NY'].includes(state.value));

  taxRateId = signal<string | null>(null);
  loading = signal(false);

  formGroup: FormGroup = this.fb.group({
    country: [{ value: 'US', disabled: true }, [Validators.required]],
    state: [null, [Validators.required]],
    city: [null, [Validators.required, Validators.maxLength(255)]],
    rate: [
      null,
      [Validators.required, Validators.min(0), Validators.max(999.99)],
    ],
  });

  constructor() {
    const city = this.formGroup.get('city')!;

    // The duplicate-location error from the API is attached to the city field,
    // so changing the state must revalidate it to clear that error.
    this.formGroup.get('state')!.valueChanges.subscribe(() => {
      if (city.hasError('serverError')) city.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.taxRateId.set(id);
      this.loadData(id);
    }
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.formGroup.get(key)?.markAsTouched();
      });
      return;
    }

    const id = this.taxRateId();
    const value = this.formGroup.getRawValue();
    const body: CreateTaxRate = {
      country: value.country,
      state: value.state,
      city: value.city,
      rate: Number(value.rate),
    };

    this.loading.set(true);

    const request$: Observable<ApiResult> = id
      ? this.taxRateService.update(id, body as UpdateTaxRate)
      : this.taxRateService.create(body);

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Taxa salva com sucesso',
          life: 2000,
        });

        this.router.navigate(['/tax-rates']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error?.message &&
                (e.error?.errors || e.error?.errors?.length > 0)) ||
              (!e.error?.message &&
                (e.error?.errors || e.error?.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup,
              );
            } else if (e.error?.message) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar a taxa',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.taxRateService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as TaxRate;
        this.formGroup.patchValue({ ...model, country: 'US' });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a taxa',
            life: 3000,
          });
        }
      },
    });
  }
}
